// src/lib/commission-taux-date.js — le taux de commission DATÉ.
//
// ⚠️ CHEMIN ARGENT. Module PUR : aucune dépendance Supabase/React, aucun état.
//
// ── Le problème ──────────────────────────────────────────────────────────
// Jusqu'ici, le taux de commission d'un logement est une valeur unique et
// INTEMPORELLE (`log.taux` en mode `fixed`, `log.paliers[]` + `log.tauxBase`
// en mode `degressive`). Tout écran qui recalcule un mois — Facturation,
// statistiques propriétaire, rapport de gestion — le recalcule avec le taux
// d'AUJOURD'HUI, y compris pour un mois déjà facturé. La pièce envoyée ne
// bouge pas (elle porte son `html_snapshot`), mais l'écran, lui, ne dit plus
// la même chose qu'elle : deux nombres visibles à la fois.
//
// ── La décision (Dimitri, 2026-09-14) ────────────────────────────────────
// Le taux devient DATÉ. Chaque barème porte le mois à partir duquel il
// s'applique ; le passé garde le sien.
//
//   log.tauxHistorique = [
//     { depuis: '2026-08', commissionMode: 'fixed',      taux: 18 },
//     { depuis: null,      commissionMode: 'degressive', paliers: [...], tauxBase: 17 },
//   ]
//
// `depuis: null` (ou absent) = « depuis toujours » : l'entrée d'ORIGINE,
// celle qui couvre tout le passé. Il y en a au plus une.
//
// ── Inertie par construction ─────────────────────────────────────────────
// Tant qu'un logement n'a PAS de `tauxHistorique` (c'est le cas des 12
// logements en production au jour de l'écriture), ce module rend EXACTEMENT
// le barème d'aujourd'hui, pour tous les mois, et les appels câblés
// retombent littéralement sur le code d'avant (drapeau `historise: false`).
// Aucune migration de données n'accompagne ce lot : le champ reste absent
// partout, donc le comportement en production est STRICTEMENT inchangé.
//
// ── Fail-closed, jamais un 0 % silencieux ────────────────────────────────
// Un barème ambigu ou introuvable LÈVE. Un vide n'est pas une valeur : mieux
// vaut un écran en erreur qu'une commission fausse imprimée sur une facture.
// Corollaire VOULU : le jour où un premier `tauxHistorique` est posé, tout
// point de calcul resté NON câblé (donc appelé sans mois) se signale par une
// exception, au lieu de rendre discrètement le mauvais taux.
//
// ⚠️ Le mois est une CLÉ `AAAA-MM`, jamais un libellé français. En base,
// `invoices.meta.mois` porte « Septembre 2026 » (produit par `formatMois` de
// lib/format.js) — ne jamais confondre les deux, la confusion a déjà produit
// un no-op silencieux dans ce dépôt.

const RE_MOIS_KEY = /^\d{4}-(0[1-9]|1[0-2])$/;

// Le mois est-il une clé `AAAA-MM` valide ?
export function estMoisKeyValide(moisKey) {
  return typeof moisKey === "string" && RE_MOIS_KEY.test(moisKey);
}

// Le logement porte-t-il un historique de taux ? (helper de lecture : les
// appelants câblés s'en servent pour ne rien changer au cas non historisé.)
export function aUnHistoriqueDeTaux(log) {
  return Array.isArray(log?.tauxHistorique) && log.tauxHistorique.length > 0;
}

// Barème « tel qu'aujourd'hui » : les champs plats du logement, inchangés.
function baremeCourant(log) {
  return {
    commissionMode: log?.commissionMode,
    taux: log?.taux,
    paliers: log?.paliers,
    tauxBase: log?.tauxBase,
    depuis: null,
    historise: false,
  };
}

// Normalise `depuis` : null / undefined / '' → null (= l'origine).
function normaliserDepuis(entree) {
  const d = entree?.depuis;
  if (d == null || d === "") return null;
  return d;
}

/**
 * Barème de commission en vigueur pour un logement à un mois donné.
 *
 * @param {object} log — le logement (`taux` / `commissionMode` / `paliers` /
 *   `tauxBase`, et optionnellement `tauxHistorique[]`).
 * @param {string} [moisKey] — clé mois `AAAA-MM`. OBLIGATOIRE dès que le
 *   logement porte un `tauxHistorique` ; ignoré sinon (rétro-compatibilité).
 * @returns {{commissionMode: string, taux: number, paliers: Array, tauxBase: number,
 *   depuis: string|null, historise: boolean}}
 *   `historise: false` ⇒ le barème rendu est LITTÉRALEMENT celui des champs
 *   plats du logement ; l'appelant peut (et doit) rester sur son chemin d'avant.
 * @throws {Error} historique ambigu (deux entrées au même `depuis`), `depuis`
 *   mal formé, mois manquant/mal formé sur un logement historisé, ou mois
 *   antérieur à toute entrée sans entrée d'origine pour le couvrir.
 */
export function resoudreCommission(log, moisKey) {
  if (!aUnHistoriqueDeTaux(log)) return baremeCourant(log);

  const hist = log.tauxHistorique;
  const nomLog = log?.id || log?.nom || "logement inconnu";

  // ── Validation de l'historique (avant toute sélection) ──
  const vues = new Set();
  for (const e of hist) {
    if (!e || typeof e !== "object") {
      throw new Error(
        `tauxHistorique de ${nomLog} : entrée invalide (attendu un objet)`,
      );
    }
    const depuis = normaliserDepuis(e);
    if (depuis !== null && !estMoisKeyValide(depuis)) {
      throw new Error(
        `tauxHistorique de ${nomLog} : « depuis » mal formé (${JSON.stringify(e.depuis)}) — attendu la clé « AAAA-MM » ou null`,
      );
    }
    const cle = depuis === null ? "__origine__" : depuis;
    if (vues.has(cle)) {
      throw new Error(
        `tauxHistorique de ${nomLog} : deux barèmes pour « ${depuis === null ? "origine" : depuis} » — un barème ambigu sur un mois est pire qu'un barème unique`,
      );
    }
    vues.add(cle);
  }

  if (!estMoisKeyValide(moisKey)) {
    throw new Error(
      `${nomLog} a un taux daté : le mois (clé « AAAA-MM ») est obligatoire pour résoudre sa commission — reçu ${JSON.stringify(moisKey)}`,
    );
  }

  // ── Sélection : la plus récente entrée datée dont `depuis` <= moisKey ──
  // Comparaison lexicographique sur « AAAA-MM » = comparaison chronologique.
  let retenue = null;
  let origine = null;
  for (const e of hist) {
    const depuis = normaliserDepuis(e);
    if (depuis === null) {
      origine = e;
      continue;
    }
    if (
      depuis <= moisKey &&
      (retenue === null || depuis > normaliserDepuis(retenue))
    ) {
      retenue = e;
    }
  }

  // Mois antérieur à toute entrée datée → l'entrée d'origine, si elle existe.
  const choisie = retenue || origine;
  if (!choisie) {
    throw new Error(
      `${nomLog} : aucun barème de commission ne couvre ${moisKey} — l'historique n'a pas d'entrée d'origine (« depuis: null ») et ne commence qu'après ce mois`,
    );
  }

  return {
    commissionMode: choisie.commissionMode,
    taux: choisie.taux,
    paliers: choisie.paliers,
    tauxBase: choisie.tauxBase,
    depuis: normaliserDepuis(choisie),
    historise: true,
  };
}
